import React, { useState, useRef, useEffect } from 'react';
import { uploadToSupabaseStorage } from '../utils/uploadUtils';

interface PDFFile {
    url: string;
    name: string;
    type?: string;
    description?: string;
}

interface PDFUploadFieldProps {
    userId: string;
    currentPDFs?: PDFFile[];
    onPDFsUpdated: (pdfs: PDFFile[]) => void;
    onError: (error: string) => void;
    maxFiles?: number;
}

export default function PDFUploadField({
    userId,
    currentPDFs = [],
    onPDFsUpdated,
    onError,
    maxFiles = 5
}: PDFUploadFieldProps) {
    const [uploading, setUploading] = useState(false);
    const [pdfs, setPdfs] = useState<PDFFile[]>(currentPDFs);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const uploadsEnabled = !!userId && userId !== 'pending';
    const materialTypes = [
        'Lecture Note',
        'Past Paper',
        'Worksheet',
        'Slide Deck',
        'Revision Guide',
        'Other'
    ];

    useEffect(() => {
        setPdfs(currentPDFs);
    }, [currentPDFs]);

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!uploadsEnabled) {
            onError('Create your account first, then upload files from your dashboard.');
            return;
        }

        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (file.type !== 'application/pdf') {
            onError('Please select a valid PDF file.');
            return;
        }

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
            onError('PDF must be less than 10MB.');
            return;
        }

        // Check max files limit
        if (pdfs.length >= maxFiles) {
            onError(`You can upload up to ${maxFiles} PDF files.`);
            return;
        }

        // Upload to Supabase Storage
        setUploading(true);

        try {
            const publicUrl = await uploadToSupabaseStorage(file, 'teacher-pdfs', userId);

            const newPdf: PDFFile = { url: publicUrl, name: file.name, type: '', description: '' };
            const updatedPdfs = [...pdfs, newPdf];
            setPdfs(updatedPdfs);
            onPDFsUpdated(updatedPdfs);
            setUploading(false);

            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        } catch (error: any) {
            onError(`Failed to upload PDF: ${error.message}`);
            setUploading(false);
        }
    };

    const handleRemove = (index: number) => {
        const updatedPdfs = pdfs.filter((_, i) => i !== index);
        setPdfs(updatedPdfs);
        onPDFsUpdated(updatedPdfs);
    };

    const updatePdfField = (index: number, field: 'type' | 'description', value: string) => {
        const updatedPdfs = pdfs.map((pdf, i) => (
            i === index ? { ...pdf, [field]: value } : pdf
        ));
        setPdfs(updatedPdfs);
        onPDFsUpdated(updatedPdfs);
    };

    return (
        <div className="pdf-upload-field" style={{ display: 'grid', gap: '0.75rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: '#475569', fontWeight: 500 }}>
                Study Materials / Notes (Optional, up to {maxFiles} PDFs)
            </label>
            <p style={{ margin: '-0.35rem 0 0', fontSize: '0.8rem', color: '#64748b', lineHeight: 1.6 }}>
                Add lecture notes, revision packs, or past papers to make your profile feel more complete.
            </p>

            {/* Uploaded PDFs List */}
            {pdfs.length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                    {pdfs.map((pdf, idx) => (
                        <div key={idx} style={{
                            padding: '0.95rem 1rem',
                            background: 'linear-gradient(180deg, #f8fafc, #ffffff)',
                            borderRadius: '16px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            border: '1px solid #e2e8f0',
                            gap: '0.75rem',
                            flexWrap: 'wrap',
                            marginBottom: '0.5rem',
                            boxShadow: '0 10px 24px rgba(15,23,42,0.04)'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: '150px' }}>
                                <div style={{
                                    width: '32px',
                                    height: '32px',
                                    background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                                    borderRadius: '6px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#fff',
                                    fontWeight: 800,
                                    fontSize: '0.6rem'
                                }}>PDF</div>
                                <span style={{ fontSize: '0.875rem', color: '#1e293b', fontWeight: 500, wordBreak: 'break-all' }}>
                                    {pdf.name}
                                </span>
                            </div>
                            <div style={{ display: 'grid', gap: '0.5rem', flex: '1 1 220px', minWidth: '200px' }}>
                                <div className="select-wrapper">
                                    <select
                                        id={`pdf-type-${idx}`}
                                        name={`pdfType-${idx}`}
                                        className="input-field"
                                        value={pdf.type || ''}
                                        onChange={(e) => updatePdfField(idx, 'type', e.target.value)}
                                        style={{ width: '100%', appearance: 'none', cursor: 'pointer' }}
                                    >
                                        <option value="">Select material type</option>
                                        {materialTypes.map((type) => (
                                            <option key={type} value={type} style={{ color: '#000' }}>{type}</option>
                                        ))}
                                    </select>
                                </div>
                                <input
                                    type="text"
                                    id={`pdf-description-${idx}`}
                                    name={`pdfDescription-${idx}`}
                                    className="input-field"
                                    placeholder="Short description (optional)"
                                    value={pdf.description || ''}
                                    onChange={(e) => updatePdfField(idx, 'description', e.target.value)}
                                />
                            </div>
                            <button
                                type="button"
                                onClick={() => handleRemove(idx)}
                                style={{
                                    background: '#fee2e2',
                                    border: 'none',
                                    color: '#ef4444',
                                    width: '28px',
                                    height: '28px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    flexShrink: 0
                                }}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Upload Button */}
            {pdfs.length < maxFiles && (
                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading || !uploadsEnabled}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.75rem 1rem',
                        background: '#fff',
                        border: '2px dashed #cbd5e1',
                        borderRadius: '14px',
                        color: '#475569',
                        cursor: uploading || !uploadsEnabled ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s',
                        width: '100%',
                        justifyContent: 'center',
                        fontWeight: 600,
                        boxShadow: '0 10px 24px rgba(15,23,42,0.04)',
                        opacity: uploadsEnabled ? 1 : 0.65
                    }}
                    onMouseEnter={(e) => {
                        if (!uploading && uploadsEnabled) {
                            e.currentTarget.style.background = '#f8fafc';
                            e.currentTarget.style.borderColor = '#6366f1';
                            e.currentTarget.style.color = '#6366f1';
                        }
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#fff';
                        e.currentTarget.style.borderColor = '#cbd5e1';
                        e.currentTarget.style.color = '#475569';
                    }}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    {uploading ? 'Uploading...' : uploadsEnabled ? 'Upload PDF' : 'Available after signup'}
                </button>
            )}

            <input
                ref={fileInputRef}
                type="file"
                id="pdf-upload-input"
                name="pdfUpload"
                accept=".pdf,application/pdf"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
            />

            {/* Progress Bar (Indeterminate) */}
            {uploading && (
                <div style={{ marginTop: '0.5rem' }}>
                    <div style={{
                        width: '100%',
                        height: '4px',
                        background: 'rgba(255,255,255,0.1)',
                        borderRadius: '2px',
                        overflow: 'hidden',
                        position: 'relative'
                    }}>
                        <div style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            height: '100%',
                            width: '50%',
                            background: 'linear-gradient(90deg, #10b981, #34d399)',
                            animation: 'indeterminate 1s infinite linear'
                        }} />
                    </div>
                    <style>{`
                        @keyframes indeterminate {
                            0% { left: -50%; width: 50%; }
                            100% { left: 100%; width: 50%; }
                        }
                    `}</style>
                    <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 500 }}>
                        Uploading to Drive...
                    </span>
                </div>
            )}

            {!uploadsEnabled && (
                <p style={{ margin: 0, fontSize: '0.78rem', color: '#64748b', lineHeight: 1.6 }}>
                    Save your teacher account first, then upload study materials from the dashboard where your storage access is authenticated.
                </p>
            )}
        </div>
    );
}
